import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../components/ui/alert-dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { FolderOpen, Plus, LogOut, Trash2, Download, ArrowLeft, Search, User, Menu, X, Users, AlertTriangle } from 'lucide-react';
import { BUSINESS_TYPES } from '../constants/businessTypes';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImplementationDashboard = ({ user, onLogout }) => {
  const [projects, setProjects] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [userRole, setUserRole] = useState('owner');
  const [loading, setLoading] = useState(false);
  const [backupLoading, setBackupLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [activeFilter, setActiveFilter] = useState('in_progress');
  const [searchQuery, setSearchQuery] = useState('');
  const [showPendingReview, setShowPendingReview] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('none');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [newProject, setNewProject] = useState({
    project_title: '',
    client_name: '',
    business_type: '',
  });
  
  // CRM link state
  const [crmDialogOpen, setCrmDialogOpen] = useState(false);
  const [crmAfmSearch, setCrmAfmSearch] = useState('');
  const [crmSearchResult, setCrmSearchResult] = useState(null);
  const [crmSearchLoading, setCrmSearchLoading] = useState(false);
  const [linkingProjectId, setLinkingProjectId] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchProjects();
    fetchTeamMembers();
    fetchUserRole();
    fetchTemplates();
  }, [activeFilter]);

  const fetchProjects = async () => {
    try {
      const response = await axios.get(`${API}/implementation/projects`, {
        params: { user_id: user.user_id, status: activeFilter }
      });
      setProjects(response.data);
    } catch (error) {
      toast.error('Σφάλμα φόρτωσης έργων');
    }
  };

  const fetchTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members?user_id=${user.user_id}`);
      setTeamMembers(response.data);
    } catch (error) {
      console.error('Error fetching team:', error);
    }
  };

  const fetchUserRole = async () => {
    try {
      const response = await axios.get(`${API}/user/info?user_id=${user.user_id}`);
      setUserRole(response.data.role || 'owner');
    } catch (error) {
      console.error('Error fetching user info:', error);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await axios.get(`${API}/implementation-templates?user_id=${user.user_id}`);
      setTemplates(response.data);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const handleAssignProject = async (projectId, memberIds) => {
    try {
      // memberIds is now an array, join with comma
      const assignedTo = Array.isArray(memberIds) ? memberIds.join(',') : (memberIds || '');
      await axios.put(`${API}/implementation/projects/${projectId}/assign?user_id=${user.user_id}&assigned_to=${assignedTo}`);
      toast.success('Το έργο ανατέθηκε');
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα ανάθεσης');
    }
  };

  // CRM Functions
  const openCrmDialog = (projectId) => {
    setLinkingProjectId(projectId);
    setCrmAfmSearch('');
    setCrmSearchResult(null);
    setCrmDialogOpen(true);
  };

  const handleCrmSearch = async () => {
    if (!crmAfmSearch || crmAfmSearch.length !== 9) {
      toast.error('Εισάγετε 9 ψηφία ΑΦΜ');
      return;
    }
    
    setCrmSearchLoading(true);
    try {
      const response = await axios.get(`${API}/crm/customers/search-by-afm?afm=${crmAfmSearch}&user_id=${user.user_id}`);
      setCrmSearchResult(response.data);
    } catch (error) {
      setCrmSearchResult(null);
      toast.error('Δεν βρέθηκε πελάτης με αυτό το ΑΦΜ');
    } finally {
      setCrmSearchLoading(false);
    }
  };

  const handleLinkCrmCustomer = async () => {
    if (!linkingProjectId || !crmSearchResult) return;
    
    // If linking to new project form, just fill the client name
    if (linkingProjectId === 'new') {
      setNewProject(prev => ({ 
        ...prev, 
        client_name: crmSearchResult.company_name,
        crm_customer_id: crmSearchResult.customer_id
      }));
      toast.success(`Επιλέχθηκε: ${crmSearchResult.company_name}`);
      setCrmDialogOpen(false);
      return;
    }
    
    // Otherwise link existing project to CRM
    try {
      await axios.put(`${API}/implementation/projects/${linkingProjectId}/link-crm?user_id=${user.user_id}&crm_customer_id=${crmSearchResult.customer_id}`);
      toast.success(`Συνδέθηκε με: ${crmSearchResult.company_name}`);
      setCrmDialogOpen(false);
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα σύνδεσης με CRM');
    }
  };

  const handleCreateProject = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const templateParam = selectedTemplateId && selectedTemplateId !== 'none' ? `&template_id=${selectedTemplateId}` : '';
      const projectData = { ...newProject };
      if (!projectData.business_type) delete projectData.business_type;
      await axios.post(`${API}/implementation/projects?user_id=${user.user_id}${templateParam}`, projectData);
      toast.success('Το έργο δημιουργήθηκε');
      setNewProject({ project_title: '', client_name: '', business_type: '' });
      setSelectedTemplateId('none');
      setDialogOpen(false);
      fetchProjects();
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Φτάσατε το μέγιστο όριο έργων. Παρακαλώ αναβαθμίστε τη συνδρομή σας.');
      } else {
        toast.error('Σφάλμα δημιουργίας έργου');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (projectId) => {
    try {
      await axios.delete(`${API}/implementation/projects/${projectId}?user_id=${user.user_id}`);
      toast.success('Το έργο διαγράφηκε');
      fetchProjects();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Σφάλμα διαγραφής έργου');
    }
  };

  const handleToggleStatus = async (projectId) => {
    try {
      await axios.put(`${API}/implementation/projects/${projectId}/toggle-status`);
      toast.success('Η κατάσταση ενημερώθηκε');
      fetchProjects();
    } catch (error) {
      toast.error('Σφάλμα ενημέρωσης');
    }
  };

  const handleBackup = async () => {
    setBackupLoading(true);
    try {
      const response = await axios.get(`${API}/implementation/user/${user.user_id}/backup`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
      link.setAttribute('download', `implementation_backup_${timestamp}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Backup ολοκληρώθηκε!');
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Άγνωστο σφάλμα';
      toast.error(`Σφάλμα δημιουργίας backup: ${errorMsg}`);
      console.error('Backup error:', error);
    } finally {
      setBackupLoading(false);
    }
  };

  const filteredProjects = projects.filter((project) => {
    if (showPendingReview && !(project.uploaded_for_review > 0)) return false;
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    return (
      project.project_title?.toLowerCase().includes(query) ||
      project.client_name?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="flex h-screen bg-slate-50">
      {/* Mobile Menu Button */}
      <button
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="md:hidden fixed top-4 left-4 z-50 p-2 bg-white rounded-lg shadow-lg border border-slate-200"
      >
        {sidebarOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
      </button>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="sidebar-overlay md:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`sidebar ${sidebarOpen ? 'sidebar-open' : ''}`}>
        <div className="p-6 border-b border-slate-200">
          <h2 className="text-xl font-bold text-slate-900">Υλοποίηση</h2>
          <p className="text-sm text-slate-600 mt-1">{user.email}</p>
        </div>

        <nav className="p-4">
          <div className="space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start"
              onClick={() => navigate('/select-module')}
              data-testid="back-to-modules-button"
            >
              <ArrowLeft className="mr-2 h-4 w-4" />
              Πίσω στην Επιλογή
            </Button>
            <div className="pt-4 border-t border-slate-200 mt-4">
              <button
                onClick={() => setActiveFilter('in_progress')}
                className={`w-full text-left px-4 py-2 rounded-md mb-1 ${activeFilter === 'in_progress' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FolderOpen className="inline mr-2 h-4 w-4" />
                Σε Εξέλιξη
              </button>
              <button
                onClick={() => setActiveFilter('completed')}
                className={`w-full text-left px-4 py-2 rounded-md ${activeFilter === 'completed' ? 'bg-slate-100 text-slate-900 font-medium' : 'text-slate-600 hover:bg-slate-50'}`}
              >
                <FolderOpen className="inline mr-2 h-4 w-4" />
                Ολοκληρωμένα
              </button>
              
              {activeFilter === 'in_progress' && (
                <button
                  onClick={() => setShowPendingReview(prev => !prev)}
                  className={`w-full text-left px-4 py-2 rounded-md mt-1 ${
                    showPendingReview
                      ? 'bg-amber-50 text-amber-800 font-medium border border-amber-200'
                      : 'text-slate-600 hover:bg-slate-50'
                  }`}
                  data-testid="filter-pending-review"
                >
                  <AlertTriangle className="inline mr-2 h-4 w-4" />
                  Με Εκκρεμότητες
                </button>
              )}
            </div>
          </div>
        </nav>

        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200">
          <Button
            onClick={onLogout}
            variant="ghost"
            className="w-full justify-start text-slate-600 hover:text-slate-900"
          >
            <LogOut className="mr-2 h-4 w-4" />
            Αποσύνδεση
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="main-content">
        <div className="p-4 md:p-8 pt-16 md:pt-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 md:mb-8 gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
                {activeFilter === 'in_progress' ? 'Έργα Υλοποίησης σε Εξέλιξη' : 'Ολοκληρωμένα Έργα Υλοποίησης'}
              </h1>
              <p className="text-slate-600 mt-1 text-sm md:text-base">
                Διαχείριση τιμολογίων και δικαιολογητικών υλοποίησης
              </p>
            </div>

            <div className="flex flex-wrap gap-2 items-center">
              {/* Search Bar */}
              <div className="relative flex-1 md:flex-none">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Αναζήτηση έργου..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 w-full md:w-64"
                  data-testid="search-input"
                />
              </div>
              <Button
                onClick={handleBackup}
                disabled={backupLoading}
                variant="outline"
                className="border-blue-600 text-blue-600 hover:bg-blue-50"
              >
                <Download className={`mr-2 h-4 w-4 ${backupLoading ? 'animate-bounce' : ''}`} />
                {backupLoading ? 'Δημιουργία...' : 'Backup Αρχείων'}
              </Button>

              {/* Confirmation Dialog - Only show for owners */}
              {userRole === 'owner' && (
              <AlertDialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
                <AlertDialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Plus className="mr-2 h-4 w-4" />
                    Νέο Έργο Υλοποίησης
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Δημιουργία Νέου Έργου Υλοποίησης</AlertDialogTitle>
                    <AlertDialogDescription>
                      Είστε σίγουροι ότι θέλετε να δημιουργήσετε νέο έργο υλοποίησης; 
                      Αυτό θα μειώσει το διαθέσιμο όριο έργων σας.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                    <AlertDialogAction onClick={() => { setConfirmDialogOpen(false); setDialogOpen(true); }}>
                      Ναι, συνέχεια
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}

              {/* Create Project Dialog */}
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Νέο Έργο Υλοποίησης</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleCreateProject} className="space-y-4">
                    <div className="space-y-2">
                      <Label htmlFor="project-title">Τίτλος Προγράμματος</Label>
                      <Input
                        id="project-title"
                        value={newProject.project_title}
                        onChange={(e) => setNewProject({ ...newProject, project_title: e.target.value })}
                        placeholder="π.χ. Ψηφιακός Μετασχηματισμός 2024"
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="client-name">Πελάτης</Label>
                      <div className="flex gap-2">
                        <Input
                          id="client-name"
                          value={newProject.client_name}
                          onChange={(e) => setNewProject({ ...newProject, client_name: e.target.value })}
                          placeholder="π.χ. ΑΒΓΔ ΙΚΕ"
                          required
                          className="flex-1"
                        />
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => openCrmDialog('new')}
                          className="text-purple-600 border-purple-200 hover:bg-purple-50"
                          data-testid="impl-search-crm-btn"
                        >
                          <Users className="h-4 w-4 mr-1" />
                          CRM
                        </Button>
                      </div>
                      <p className="text-xs text-slate-500">
                        Ή αναζητήστε πελάτη από το CRM με ΑΦΜ
                      </p>
                    </div>

                    {/* Template Selection */}
                    {templates.length > 0 && (
                      <div className="space-y-2">
                        <Label htmlFor="template">Πρότυπο (προαιρετικό)</Label>
                        <Select value={selectedTemplateId} onValueChange={setSelectedTemplateId}>
                          <SelectTrigger>
                            <SelectValue placeholder="Επιλέξτε πρότυπο..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Χωρίς πρότυπο</SelectItem>
                            {templates.map(template => (
                              <SelectItem key={template.template_id} value={template.template_id}>
                                {template.name} ({template.document_count || template.documents?.length || 0} δικαιολογητικά)
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-slate-500">
                          Τα δικαιολογητικά του προτύπου θα προστεθούν αυτόματα στα Γενικά Δικαιολογητικά
                        </p>
                      </div>
                    )}

                    {/* Business Type Selection */}
                    <div className="space-y-2">
                      <Label htmlFor="impl-business-type">Μορφή Επιχείρησης (προαιρετικό)</Label>
                      <Select
                        value={newProject.business_type || "none"}
                        onValueChange={(val) => setNewProject({ ...newProject, business_type: val === "none" ? "" : val })}
                      >
                        <SelectTrigger data-testid="impl-business-type-select">
                          <SelectValue placeholder="Επιλέξτε μορφή..." />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Χωρίς επιλογή</SelectItem>
                          {BUSINESS_TYPES.map(bt => (
                            <SelectItem key={bt} value={bt}>{bt}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-slate-500">
                        Φιλτράρει τα δικαιολογητικά του προτύπου βάσει μορφής επιχείρησης
                      </p>
                    </div>

                    <Button type="submit" className="w-full bg-blue-600 hover:bg-blue-700" disabled={loading}>
                      {loading ? 'Δημιουργία...' : 'Δημιουργία Έργου'}
                    </Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Projects Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredProjects.map((project) => (
              <div
                key={project.project_id}
                className="bg-white border border-slate-200 rounded-md shadow-sm hover:shadow-md transition-shadow cursor-pointer group"
              >
                <div 
                  className="p-6"
                  onClick={() => navigate(`/implementation/projects/${project.project_id}`)}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-md flex items-center justify-center">
                      <FolderOpen className="h-6 w-6 text-blue-600" />
                    </div>
                    <span className={`text-xs font-medium px-2 py-1 rounded-full ${
                      project.status === 'completed' 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-blue-100 text-blue-700'
                    }`}>
                      {project.status === 'completed' ? 'Ολοκληρωμένο' : 'Σε Εξέλιξη'}
                    </span>
                  </div>

                  <h3 className="font-semibold text-slate-900 mb-1 group-hover:text-blue-600 transition-colors">
                    {project.project_title}
                  </h3>
                  <p className="text-sm text-slate-600 mb-3">{project.client_name}</p>
                  
                  <div className="flex flex-wrap gap-1 mb-2">
                    {project.pending_documents > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-amber-50 text-amber-700 text-xs font-medium">
                        {project.pending_documents} εκκρεμή
                      </span>
                    )}
                    {project.uploaded_for_review > 0 && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-blue-50 text-blue-700 text-xs font-medium">
                        {project.uploaded_for_review} για έλεγχο
                      </span>
                    )}
                  </div>

                  <div className="text-xs text-slate-500 space-y-1">
                    <div>Τιμολόγια: {project.invoice_count || 0}</div>
                    {project.assigned_to_names && project.assigned_to_names.length > 0 && (
                      <div className="flex items-center gap-1">
                        <User className="h-3 w-3" />
                        <span>{project.assigned_to_names.join(', ')}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="px-6 py-3 bg-slate-50 border-t border-slate-200 space-y-2">
                  {/* Multi-select assignment - only for owners */}
                  {userRole === 'owner' && teamMembers.length > 0 && (
                    <div className="space-y-1" onClick={(e) => e.stopPropagation()}>
                      <p className="text-xs font-medium text-slate-600">Ανάθεση:</p>
                      <div className="max-h-24 overflow-y-auto border border-slate-200 rounded p-1.5 bg-white space-y-0.5">
                        {teamMembers.map((member) => {
                          const currentAssigned = Array.isArray(project.assigned_to) ? project.assigned_to : [];
                          const isChecked = currentAssigned.includes(member.user_id);
                          return (
                            <label 
                              key={member.user_id} 
                              className="flex items-center gap-1.5 p-0.5 hover:bg-slate-50 rounded cursor-pointer"
                            >
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  let newAssigned;
                                  if (e.target.checked) {
                                    newAssigned = [...currentAssigned, member.user_id];
                                  } else {
                                    newAssigned = currentAssigned.filter(id => id !== member.user_id);
                                  }
                                  handleAssignProject(project.project_id, newAssigned);
                                }}
                                className="rounded border-slate-300 text-blue-600 focus:ring-blue-500 h-3 w-3"
                              />
                              <span className="text-xs text-slate-700">{member.full_name || member.email}</span>
                            </label>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleToggleStatus(project.project_id);
                      }}
                      className="text-xs text-slate-600 hover:text-slate-900"
                    >
                      {project.status === 'in_progress' ? 'Ολοκλήρωση' : 'Επαναφορά'}
                    </button>
                  
                  {/* Delete button - Only show for owners */}
                  {userRole === 'owner' && (
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <button
                        onClick={(e) => e.stopPropagation()}
                        className="text-rose-600 hover:text-rose-700"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </AlertDialogTrigger>
                    <AlertDialogContent>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Διαγραφή Έργου</AlertDialogTitle>
                        <AlertDialogDescription>
                          Είστε σίγουροι ότι θέλετε να διαγράψετε το έργο "{project.project_title}"; Θα διαγραφούν όλα τα τιμολόγια και τα αρχεία.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel>Ακύρωση</AlertDialogCancel>
                        <AlertDialogAction
                          onClick={() => handleDeleteProject(project.project_id)}
                          className="bg-rose-600 hover:bg-rose-700"
                        >
                          Διαγραφή
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                  )}
                  </div>
                </div>
              </div>
            ))}

            {filteredProjects.length === 0 && (
              <div className="col-span-full text-center py-12 text-slate-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                <p>Δεν υπάρχουν έργα υλοποίησης</p>
                <p className="text-sm mt-1">
                  {userRole === 'owner' 
                    ? 'Δημιουργήστε το πρώτο σας έργο' 
                    : 'Δεν έχουν ανατεθεί έργα ακόμα'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CRM Link Dialog */}
      <Dialog open={crmDialogOpen} onOpenChange={setCrmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Αναζήτηση Πελάτη από CRM</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Αναζήτηση με ΑΦΜ (9 ψηφία)</Label>
              <div className="flex gap-2">
                <Input
                  value={crmAfmSearch}
                  onChange={(e) => setCrmAfmSearch(e.target.value.replace(/\D/g, '').slice(0, 9))}
                  placeholder="123456789"
                  maxLength={9}
                  data-testid="impl-crm-afm-search"
                />
                <Button 
                  onClick={handleCrmSearch} 
                  disabled={crmSearchLoading || crmAfmSearch.length !== 9}
                >
                  {crmSearchLoading ? '...' : 'Αναζήτηση'}
                </Button>
              </div>
            </div>

            {crmSearchResult && (
              <div className="p-4 bg-slate-50 rounded-lg border">
                <h4 className="font-medium text-slate-900">{crmSearchResult.company_name}</h4>
                <p className="text-sm text-slate-600">ΑΦΜ: {crmSearchResult.tax_id}</p>
                {crmSearchResult.contact_person && (
                  <p className="text-sm text-slate-600">Υπεύθυνος: {crmSearchResult.contact_person}</p>
                )}
                {crmSearchResult.phone && (
                  <p className="text-sm text-slate-600">Τηλ: {crmSearchResult.phone}</p>
                )}
                <Button 
                  className="mt-3 w-full" 
                  onClick={handleLinkCrmCustomer}
                  data-testid="impl-confirm-crm-link"
                >
                  Επιλογή πελάτη
                </Button>
              </div>
            )}

            <p className="text-xs text-slate-500">
              Αναζητήστε πελάτη από το CRM με ΑΦΜ. Η επωνυμία θα συμπληρωθεί αυτόματα.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ImplementationDashboard;
